import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, CircularProgress, Popover, Typography } from '@mui/material'
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { toast } from 'react-toastify'
import {
  DESK_CALL_ROLES,
  describeDeskCallStatus,
  getDeskCall,
  placeDeskCall,
  previewDeskCall,
  startDeskCallRequest,
} from 'src/clinic/services/deskCallService'
import {
  getPortalVoiceFriendlyErrorMessage,
  subscribePortalVoiceCallState,
} from 'src/mentor/components/voice/portalVoiceCallManager'
import DeskCallerVerification from './DeskCallerVerification'

// "Call the screener on duty". The desk never picks a number, and unless it taps a rail card, not
// even a person: the server resolves who is checked in / rostered for the role. The popover shows
// who would be called before dialling, then follows the request after the call ends (no answer →
// WhatsApp fallback already sent → offer the next candidate).

const OUTCOME_POLL_MS = 2500
const OUTCOME_POLL_MAX = 14

function candidateHeadline(candidate, role, preview) {
  if (!candidate) {
    const label = DESK_CALL_ROLES[role]?.label?.toLowerCase() || 'practitioner'
    // Nobody now and nobody later today: say so plainly rather than "on duty" ambiguity.
    if (preview && !preview.nextRosterStartAt) {
      return `No ${label} available today`
    }
    return `No ${label} on duty right now`
  }
  return candidate?.name || 'Practitioner on duty'
}

function formatClock(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

export default function DeskCallButton({
  clinicBrandId,
  clinicId,
  role = 'screener',
  chamberRoomId,
  eventType,
  specialtySlug,
  doctorMentorId,
  bookedEventId,
  practitionerId,
  label,
  heading,
  reason,
  size = 'small',
  variant = 'outlined',
  fullWidth = false,
  compact = false,
  sx,
  onRequestChange,
  // Optional custom trigger: ({ onClick, label, busy }) => element. Used by booking strips.
  renderTrigger,
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [preview, setPreview] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [placing, setPlacing] = useState(false)
  const [activeRequest, setActiveRequest] = useState(null)
  const [callState, setCallState] = useState(null)
  const [triedPractitionerIds, setTriedPractitionerIds] = useState([])
  const pollRef = useRef(null)
  const open = Boolean(anchorEl)
  const roleMeta = DESK_CALL_ROLES[role] || DESK_CALL_ROLES.screener

  useEffect(() => subscribePortalVoiceCallState(setCallState), [])

  useEffect(
    () => () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
      }
    },
    [],
  )

  const loadPreview = useCallback(
    async (skipIds = []) => {
      if (!clinicId) return
      setPreviewLoading(true)
      const result = await previewDeskCall({
        clinicBrandId,
        clinicId,
        role,
        chamberRoomId,
        eventType,
        specialtySlug,
        doctorMentorId,
        skipPractitionerIds: skipIds,
      })
      setPreview(result)
      setPreviewLoading(false)
    },
    [clinicBrandId, clinicId, role, chamberRoomId, eventType, specialtySlug, doctorMentorId],
  )

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget)
    setActiveRequest(null)
    setTriedPractitionerIds([])
    loadPreview([])
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  // Once the softphone call for our request ends, follow the request until the server settles it
  // (answered / missed / escalated) so the desk sees the outcome and the next step.
  const followOutcome = useCallback(
    (requestId) => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
      }
      let attempts = 0
      pollRef.current = setInterval(async () => {
        attempts += 1
        const result = await getDeskCall(requestId)
        if (result?.ok && result?.deskCallRequest) {
          setActiveRequest(result.deskCallRequest)
          onRequestChange?.(result.deskCallRequest)
          const settled = !['ringing', 'resolving'].includes(result.deskCallRequest.status)
          if (settled || attempts >= OUTCOME_POLL_MAX) {
            clearInterval(pollRef.current)
            pollRef.current = null
          }
        } else if (attempts >= OUTCOME_POLL_MAX) {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
      }, OUTCOME_POLL_MS)
    },
    [onRequestChange],
  )

  useEffect(() => {
    if (!activeRequest?.id) return
    if (callState?.status === 'ended' && ['ringing', 'resolving'].includes(activeRequest?.status)) {
      followOutcome(activeRequest.id)
    }
  }, [callState?.status, activeRequest?.id, activeRequest?.status, followOutcome])

  const handleCall = async ({ skipIds = [], explicitPractitionerId = null, retryOfRequestId = null } = {}) => {
    setPlacing(true)
    try {
      const result = await placeDeskCall({
        clinicBrandId,
        clinicId,
        role,
        chamberRoomId,
        eventType,
        specialtySlug,
        doctorMentorId,
        bookedEventId,
        practitionerId: explicitPractitionerId || practitionerId || undefined,
        skipPractitionerIds: skipIds,
        retryOfRequestId,
        reason,
      })
      if (result?.deskCallRequest) {
        setActiveRequest(result.deskCallRequest)
        onRequestChange?.(result.deskCallRequest)
        if (result.deskCallRequest?.targetPractitionerId) {
          setTriedPractitionerIds((prev) => [...prev, result.deskCallRequest.targetPractitionerId])
        }
      }
      if (result?.nobodyOnDuty) {
        toast.info(result?.message || `Nobody is on duty for ${roleMeta.label.toLowerCase()} right now.`)
      } else {
        toast.info(`Calling ${result?.deskCallRequest?.targetDisplayName || roleMeta.article}…`)
      }
    } catch (error) {
      toast.error(getPortalVoiceFriendlyErrorMessage(error, 'Unable to place the call right now.'))
    } finally {
      setPlacing(false)
    }
  }

  const handleNotifyOnly = async (targetPractitionerId = null) => {
    setPlacing(true)
    try {
      const result = await startDeskCallRequest({
        clinicBrandId,
        clinicId,
        role,
        chamberRoomId,
        eventType,
        specialtySlug,
        doctorMentorId,
        bookedEventId,
        practitionerId: targetPractitionerId || practitionerId || undefined,
        skipPractitionerIds: targetPractitionerId ? [] : triedPractitionerIds,
        reason,
        notifyOnly: true,
      })
      if (!result?.ok) {
        toast.error(result?.message || 'Unable to send the WhatsApp message.')
        return
      }
      if (result?.nobodyOnDuty) {
        toast.info(result?.message || 'Nobody is on duty to message.')
        return
      }
      setActiveRequest(result?.deskCallRequest || null)
      onRequestChange?.(result?.deskCallRequest || null)
      toast.success(`${result?.deskCallRequest?.targetDisplayName || 'The practitioner'} has been messaged on WhatsApp.`)
    } finally {
      setPlacing(false)
    }
  }

  const primary = preview?.primary || null
  const nextCandidate = useMemo(() => {
    const candidates = preview?.candidates || []
    return candidates.find((candidate) => !triedPractitionerIds.includes(candidate?.practitionerId)) || null
  }, [preview?.candidates, triedPractitionerIds])
  const routeDisabledReason = preview?.route && preview.route.enabled === false ? preview.route.disabledReason : ''
  const callingDisabled =
    placing ||
    previewLoading ||
    !primary ||
    Boolean(routeDisabledReason) ||
    preview?.permissionEnabled === false ||
    preview?.deskCallEnabled === false ||
    ['connecting', 'ringing', 'connected', 'reconnecting', 'on-hold', 'ending'].includes(callState?.status)
  const requestSettled = activeRequest && !['ringing', 'resolving'].includes(activeRequest.status)
  const requestMissed = ['missed', 'notified', 'escalated', 'nobody-on-duty'].includes(activeRequest?.status)
  const nobodyToday = Boolean(preview?.ok && !primary && !preview?.nextRosterStartAt)
  const triggerLabel = label || `Call ${roleMeta.label.toLowerCase()}`

  return (
    <>
      {typeof renderTrigger === 'function' ? (
        renderTrigger({ onClick: handleOpen, label: triggerLabel, busy: placing })
      ) : (
        <Button
          size={size}
          variant={variant}
          color="primary"
          fullWidth={fullWidth}
          startIcon={compact ? undefined : <PhoneInTalkIcon fontSize="small" />}
          onClick={handleOpen}
          sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', ...sx }}
          aria-label={label || `Call ${roleMeta.article} on duty`}
        >
          {compact ? <PhoneInTalkIcon fontSize="small" /> : triggerLabel}
        </Button>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 320, p: 2, borderRadius: '14px' } } }}
      >
        <Typography sx={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'rgba(0,0,0,0.5)' }}>
          {heading || `${roleMeta.label} on duty`}
        </Typography>
        {reason ? (
          <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)', mt: 0.25 }}>{reason}</Typography>
        ) : null}

        {previewLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: '13px' }}>Finding who is on duty…</Typography>
          </Box>
        ) : preview && !preview.ok ? (
          <Typography sx={{ fontSize: '13px', color: '#b91c1c', py: 1 }}>{preview.message}</Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: '16px', fontWeight: 800, mt: 0.5, color: nobodyToday ? '#b91c1c' : undefined }}>
              {candidateHeadline(primary, role, preview)}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)' }}>
              {primary?.statusLabel ||
                (preview?.nextRosterStartAt
                  ? `${preview.nextRosterPractitionerName || 'The next one'} is rostered at ${formatClock(preview.nextRosterStartAt)}`
                  : `Nobody is checked in or rostered as ${roleMeta.article} for the rest of today. Ask support to arrange one.`)}
            </Typography>
            {preview?.permissionEnabled === false ? (
              <Typography sx={{ fontSize: '12px', color: '#b45309', mt: 1 }}>
                Calling the {roleMeta.label.toLowerCase()} on duty is not enabled for your assignment. Ask support to enable it.
              </Typography>
            ) : routeDisabledReason ? (
              <Typography sx={{ fontSize: '12px', color: '#b45309', mt: 1 }}>{routeDisabledReason}</Typography>
            ) : null}

            {activeRequest ? (
              <Box sx={{ mt: 1.5, p: 1.25, borderRadius: '10px', backgroundColor: 'rgba(15, 118, 110, 0.06)' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                  {activeRequest.targetDisplayName || roleMeta.label}: {describeDeskCallStatus(activeRequest)}
                </Typography>
                {activeRequest.outcomeDetail ? (
                  <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)' }}>{activeRequest.outcomeDetail}</Typography>
                ) : null}
                {activeRequest.acknowledgement ? (
                  <Typography sx={{ fontSize: '12px', color: '#047857', fontWeight: 700 }}>
                    {activeRequest.targetDisplayName || 'They'} said they are on their way.
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
              {!activeRequest || requestSettled ? (
                requestMissed && nextCandidate && nextCandidate.practitionerId !== activeRequest?.targetPractitionerId ? (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={placing}
                    startIcon={<PhoneInTalkIcon fontSize="small" />}
                    onClick={() =>
                      handleCall({
                        skipIds: triedPractitionerIds,
                        retryOfRequestId: activeRequest?.id || null,
                      })
                    }
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Try {nextCandidate.name} ({nextCandidate.statusLabel})
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={callingDisabled}
                    startIcon={placing ? <CircularProgress size={14} color="inherit" /> : <PhoneInTalkIcon fontSize="small" />}
                    onClick={() => handleCall({ retryOfRequestId: activeRequest?.id || null })}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    {activeRequest ? 'Call again' : `Call ${primary?.firstName || primary?.name || roleMeta.article}`}
                  </Button>
                )
              ) : (
                <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)' }}>
                  Ringing {activeRequest.targetDisplayName || roleMeta.article}… use the call panel to hang up.
                </Typography>
              )}
              {primary && (!activeRequest || requestSettled) ? (
                <Button
                  variant="text"
                  size="small"
                  disabled={placing || preview?.permissionEnabled === false}
                  startIcon={<WhatsAppIcon fontSize="small" />}
                  onClick={() => handleNotifyOnly(requestMissed && nextCandidate ? nextCandidate.practitionerId : null)}
                  sx={{ textTransform: 'none', fontWeight: 600, justifyContent: 'flex-start' }}
                >
                  {requestMissed && nextCandidate
                    ? `Message ${nextCandidate.firstName || nextCandidate.name} on WhatsApp`
                    : 'Message on WhatsApp instead'}
                </Button>
              ) : null}
            </Box>

            <Typography sx={{ fontSize: '11px', color: 'rgba(0,0,0,0.45)', mt: 1.5 }}>
              Calls connect through AUMHUM. Neither side sees the other&apos;s number; they hear “call from the
              front desk at {preview?.clinicLocationName || 'your clinic'}” and press 1 to accept.
            </Typography>
            <DeskCallerVerification clinicId={clinicId} />
          </>
        )}
      </Popover>
    </>
  )
}

DeskCallButton.propTypes = {
  clinicBrandId: PropTypes.string,
  clinicId: PropTypes.string,
  role: PropTypes.oneOf(['screener', 'therapist', 'doctor']),
  chamberRoomId: PropTypes.string,
  eventType: PropTypes.string,
  specialtySlug: PropTypes.string,
  doctorMentorId: PropTypes.string,
  bookedEventId: PropTypes.string,
  practitionerId: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.string,
  variant: PropTypes.string,
  fullWidth: PropTypes.bool,
  compact: PropTypes.bool,
  sx: PropTypes.object,
  onRequestChange: PropTypes.func,
}
